import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "company-info" }
const _hoisted_2 = { class: "company-info" }
const _hoisted_3 = { id: "company-category-settings" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("h1", null, [
  /*#__PURE__*/_createElementVNode("span", { class: "page-title" }, "Cacher des catégories"),
  /*#__PURE__*/_createElementVNode("span", {
    class: "lg:tooltip title-tooltip",
    "data-tip": "Les catégories cochées seront cachées pour les utilisateurs de cette société cliente."
  }, [
    /*#__PURE__*/_createElementVNode("i", {
      class: "pi pi-exclamation-circle",
      style: {"color":"orange","font-size":"1.5rem"}
    })
  ])
], -1)

import { ref, onMounted, watch } from "vue";
import ApiService from "@/_services/api.service";
import swal from "../../../_helpers/swal";
import Tree from "primevue/tree";
import Button from "primevue/button";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";

type SelectedTreeNodePrimeVueEventType = {
    [key: string]: {
        checked: boolean;
        partialChecked: boolean;
    };
};

type SelectedCategoryType = {
    [key: string]: {
        key: string;
        label: string;
        checked: boolean;
        partialChecked: boolean;
        uuid: string;
    };
};

interface ICategory {
    key: string;
    uuid: string;
    label: string;
    isActive: boolean;
    children?: [] | ICategory[];
}

interface ICompanyCategorySetting {
    categoryUuid: string;
}

interface ICompanyInfos {
    name: string;
    accountManager: string | null;
    uuid: string;
    totalActiveUsers: number;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CompanyCategorySettings',
  setup(__props) {

const selectedCategoriesKeys = ref<SelectedTreeNodePrimeVueEventType>({});
const selectedCategoriesKeysMap = ref(new Map());
const categoriesKeysMapOnInit = ref(new Map());
const companyUuid = ref<string | null>(null);
const companyInfos = ref<ICompanyInfos>({
    name: "",
    accountManager: "",
    uuid: "",
    totalActiveUsers: 0,
});
const isLoadingCategories = ref<boolean>(true);
const isCategoriesSettingEmpty = ref<boolean>(true);
const isInitCategoriesStateChanges = ref<boolean>(false);
const categoriesLevel1Nodes = ref(Array<ICategory>());
const expandedCategoriesKeys = ref({});
const categoriesAndChildrenNodes = ref(Array<ICategory>());
const categoriesSettingNodes = ref(Array<ICompanyCategorySetting>());
const categoriesSettingMapNodes = ref(new Map());
const route = useRoute();
const router = useRouter();

watch(
    selectedCategoriesKeysMap,
    (newValue) => {
        selectedCategoriesKeys.value = transformMapToObject(newValue);
    },
    { deep: true }
);

watch(
    categoriesSettingNodes,
    (newValue) => {
        categoriesSettingMapNodes.value = transformObjectToMap(newValue);
    },
    { deep: true }
);

watch(companyUuid, () => {
    if (isCompanyUuidDefined()) {
        fetchCategoriesData();
    }
});

onMounted(() => {
    companyUuid.value = route.params.uuid as string;
});

function initSelectedCategoriesKeys() {
    prepareCategorySettingsData();
    hydrateCategoriesTreeview();
}

function prepareCategorySettingsData() {
    categoriesAndChildrenNodes.value.forEach((category) => {
        const catSettingMatchingCategory = categoriesSettingNodes.value.find(
            (setting) => setting.categoryUuid === category.uuid
        );

        if (catSettingMatchingCategory) {
            category.isActive = true;
        }

        categoriesKeysMapOnInit.value.set(category.key, {
            key: category.key,
            label: category.label,
            checked: category.isActive,
            partialChecked: false,
            uuid: category.uuid,
        });
    });
}

function hydrateCategoriesTreeview() {
    categoriesLevel1Nodes.value.forEach((category: ICategory) => {
        expandedCategoriesKeys.value[category.key] = true;
    });

    categoriesAndChildrenNodes.value.forEach((category: ICategory) => {
        if (!selectedCategoriesKeysMap.value.has(category.key)) {
            selectedCategoriesKeysMap.value.set(category.key, {
                checked: category.isActive,
                partialChecked: false,
            });
        }
    });
}

function onUpdateSelectedCategories(event) {
    isInitCategoriesStateChanges.value = true;
    selectedCategoriesKeysMap.value = transformObjectToMap(event);
}

async function saveCategories(event) {
    event.preventDefault();

    const checkedCategories = new Map<string, { uuid: string }>();
    selectedCategoriesKeysMap.value.forEach(
        (updatedNode: SelectedCategoryType, updatedKey: string) => {
            if (updatedNode.checked) {
                categoriesAndChildrenNodes.value.forEach((category: ICategory) => {
                    if (category.key === updatedKey) {
                        checkedCategories.set(updatedKey, { uuid: category.uuid });
                    }
                });
            }
        }
    );

    let dataToSave = {
        uuid: companyUuid.value,
        categories: Array.from(checkedCategories.values()),
    };

    await ApiService.patchWithoutSlug("company_settings", dataToSave)
        .then((response) => {
            swal.info("Les modifications ont bien été prises en compte");
            isInitCategoriesStateChanges.value = false;
        })
        .catch(() => {
            swal.error();
        });
}

async function confirmLeavePage() {
    return new Promise((resolve) => {
        swal
            .confirm(
                "Êtes-vous sûr de vouloir quitter cette page ? Les modifications non enregistrées seront perdues"
            )
            .then((result) => {
                resolve(result.isConfirmed);
            });
    });
}

onBeforeRouteLeave((to, from, next) => {
    if (isInitCategoriesStateChanges.value) {
        confirmLeavePage().then((shouldLeavePage) => {
            shouldLeavePage ? next() : next(false);
        });
    } else {
        next();
    }
});

async function fetchCategoriesData() {
    if (isCompanyUuidDefined()) {
        await ApiService.get("categories", {
            params: { lvl: 1, "order[lft]": "ASC" },
        })
            .then(async ({ data }) => {
                if (data.data.length > 0) {
                    data.data.forEach((category, index: number) => {
                        let id = index.toString();
                        let newCategory: ICategory = {
                            key: id,
                            uuid: category.uuid,
                            label: category.name,
                            isActive: false,
                            children: formatChildrenToCategory(category.children, id),
                        };
                        categoriesLevel1Nodes.value.push(newCategory);
                        categoriesAndChildrenNodes.value.push(newCategory);
                    });

                    const categoriesSettings = await ApiService.get(
                        "company_settings/company/" + companyUuid.value,
                        {}
                    );
                    if (categoriesSettings.data.data.length > 0) {
                        isCategoriesSettingEmpty.value = false;
                        categoriesSettings.data.data.forEach((categorySetting) => {
                            let newCategorySetting: ICompanyCategorySetting = {
                                categoryUuid: categorySetting.category.replace(
                                    /^\/api\/categories\//,
                                    ""
                                ),
                            };
                            categoriesSettingNodes.value.push(newCategorySetting);
                        });
                    }

                    const companyData = await ApiService.get(
                        "companies/" + companyUuid.value,
                        {}
                    );
                    companyInfos.value = companyData.data.data;
                    initSelectedCategoriesKeys();
                }
            })
            .catch(() => {
                swal.error("Une erreur a été détectée, veuillez re essayer 4.");
                return false;
            });
        isLoadingCategories.value = false;
    } else {
        router.push({ name: "categories-settings-list" });
    }
}

function formatChildrenToCategory(data, parentKey: string) {
    let childrenData = Array<ICategory>();
    data.forEach((item, index: number) => {
        let key = parentKey + "-" + index;
        let newCategory: ICategory = {
            key: key,
            uuid: item.uuid,
            label: item.name,
            isActive: false,
            children:
                item.children && item.children.length
                    ? formatChildrenToCategory(item.children, key)
                    : [],
        };
        childrenData.push(newCategory);
        categoriesAndChildrenNodes.value.push(newCategory);
    });

    return childrenData;
}

function isCompanyUuidDefined() {
    return companyUuid.value?.length;
}

function transformMapToObject(mapObject) {
    return Object.fromEntries(mapObject);
}

function transformObjectToMap(object) {
    return new Map(Object.entries(object));
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", null, [
      _createElementVNode("p", _hoisted_1, "Account Manager: " + _toDisplayString(companyInfos.value.accountManager), 1),
      _createElementVNode("p", _hoisted_2, _toDisplayString(companyInfos.value.totalActiveUsers) + " utilisateurs actifs", 1)
    ]),
    _createElementVNode("div", _hoisted_3, [
      _hoisted_4,
      _createElementVNode("div", null, [
        _createVNode(_unref(Tree), {
          selectionKeys: selectedCategoriesKeys.value,
          expandedKeys: expandedCategoriesKeys.value,
          value: categoriesLevel1Nodes.value,
          loading: isLoadingCategories.value,
          selectionMode: "checkbox",
          "onUpdate:selectionKeys": _cache[0] || (_cache[0] = ($event: any) => (onUpdateSelectedCategories($event))),
          class: "w-full md:w-30rem"
        }, null, 8, ["selectionKeys", "expandedKeys", "value", "loading"]),
        _createVNode(_unref(Button), {
          class: "btn btn-primary p-2 ps-2 pe-2",
          disabled: !isInitCategoriesStateChanges.value,
          id: "saveCategoriesBtn",
          label: "Enregistrer les modifications",
          "aria-label": "Enregistrer les modifications",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (saveCategories($event)))
        }, null, 8, ["disabled"])
      ])
    ])
  ], 64))
}
}

})